import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import { useShowToast } from '@/design_system/Toast';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconCross from '@/icons/Cross.svg';
import { useEntities } from '@/models/entity';
import { Role, useRoles } from '@/models/role';
import { useInviteUser, useUpdateInvite } from '@/models/userRole';
import { useCurrentOrganization } from '@/services/auth';
import { isEmailValid } from '@/utils/email';
import useDebouncedState from '@/utils/useDebouncedState';

/**
 * Used for both actual users and invited members which aren't users yet.
 */
export type UserWithRoles = {
  id?: string; // Only for existing users
  name?: string; // Only for existing users
  email: string;
  roles: UserRole[];
};

type UserRole = {
  id: string;
  roleId: string;
  role: Role;
  organizationId: string | null;
  entityId: string | null;
  entity: {
    name: string;
  } | null;
};

export const UserDialog = ({
  isOpen,
  setIsOpen,
  user,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  user?: UserWithRoles;
}) => {
  const { t } = useLingui();
  const showToast = useShowToast();

  const [mutateError, setMutateError] = useState<string | null>(null);

  const { mutateAsync: inviteUser, isPending: isInviteUserPending } = useInviteUser();
  const { mutateAsync: updateInvite, isPending: isUpdateInvitePending } = useUpdateInvite();

  const isPending = isInviteUserPending || isUpdateInvitePending;

  const submit = async ({
    userId,
    email,
    userRoles,
  }: {
    userId?: string;
    email?: string;
    userRoles: {
      id: string | null;
      roleId: string;
      organizationId: string | null;
      entityId: string | null;
    }[];
  }) => {
    setMutateError(null);

    try {
      if (userId) {
        await updateInvite({
          userId,
          userRoles,
        });

        setIsOpen(false);
      } else {
        await inviteUser({
          email: email!,
          userRoles,
        });

        showToast({
          text: t({
            id: 'settings.users.invitation.success',
            message: `Invitation sent to ${email}`,
          }),
          type: 'success',
        });

        setIsOpen(false);
      }
    } catch (error) {
      setMutateError(
        (error as Error).message ??
          t({
            id: 'shipment.unknown-error',
            message: 'Unknown error. Please contact Support',
          })
      );
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          if (isPending) return;
          setIsOpen(false);
        }
      }}
      title={
        user ? (
          <p className="sentry-mask">
            {t({
              id: 'settings.users.update-modal.title',
              message: `Manage ${user.name ?? user.email}`,
            })}
          </p>
        ) : (
          t({ id: 'settings.users.invite-modal.title', message: 'Invite a user' })
        )
      }
      style={{ width: '50rem' }}
    >
      <UserDialogForm
        user={user}
        onSubmit={submit}
        isPendingSubmit={isPending}
        cancel={() => setIsOpen(false)}
        mutateError={mutateError}
      />
    </Dialog>
  );
};

const UserDialogForm = ({
  user,
  onSubmit,
  isPendingSubmit,
  cancel,
  mutateError,
}: {
  user?: UserWithRoles;
  onSubmit: ({
    userId,
    email,
    userRoles,
  }: {
    userId?: string;
    email?: string;
    userRoles: {
      id: string | null;
      roleId: string;
      organizationId: string | null;
      entityId: string | null;
    }[];
  }) => Promise<void>;
  isPendingSubmit: boolean;
  cancel: () => void;
  mutateError: string | null;
}) => {
  const { t } = useLingui();

  const [showErrors, setShowErrors] = useState(false);

  const [email, setEmail] = useState(user?.email ?? '');
  const [userRoles, setUserRoles] = useState<UserRole[]>(user?.roles ?? []);

  const noEmail =
    !email || !isEmailValid(email)
      ? t({
          id: 'settings.users.invite-modal.error.no-email',
          message: 'Please enter a valid email',
        })
      : undefined;
  const noRoles =
    userRoles.length === 0
      ? t({
          id: 'settings.users.invite-modal.error.no-roles',
          message: 'At least one role is required',
        })
      : undefined;
  const roleWithScopeError = userRoles.find((userRole) => {
    if (userRole.role.scopeStore && !userRole.entityId) return true;
    if (userRole.role.scopeWorkshop && !userRole.entityId) return true;
    if (userRole.role.scopeAfterSales && !userRole.entityId) return true;

    return false;
  })
    ? t({
        id: 'settings.users.invite-modal.error.role-with-scope',
        message: 'Please assign an entity to all roles that requires it',
      })
    : undefined;
  const hasStateError = noEmail || noRoles || roleWithScopeError;

  const handleSubmit = () => {
    if (hasStateError) {
      setShowErrors(true);

      return;
    }

    onSubmit({
      userId: user?.id ? user.id : undefined,
      email: user?.id ? undefined : email,
      userRoles: userRoles.map((userRole) => ({
        id: userRole.id.startsWith('new-') ? null : userRole.id,
        roleId: userRole.roleId,
        organizationId: userRole.organizationId ?? null,
        entityId: userRole.entityId ?? null,
      })),
    });
  };

  return (
    <>
      <main>
        <Stack gap="24px" style={{ minHeight: '20rem' }}>
          <InputText
            label={<Trans id="settings.users.invite-modal.field.email.label">Email</Trans>}
            type="email"
            placeholder="user@example.com"
            value={email}
            onChange={setEmail}
            isRequired
            isDisabled={isPendingSubmit || !!user}
            isInvalid={showErrors && !!noEmail}
            error={showErrors ? noEmail : undefined}
          />
          <UserRolesInput
            userRoles={userRoles}
            setUserRoles={setUserRoles}
            showErrors={showErrors}
          />
          {!user && (
            <Paragraph>
              <Trans id="settings.users.invite-modal.disclaimer">
                The invited user will receive <b>an email with a link</b> to set up their account.
              </Trans>
            </Paragraph>
          )}
          {((showErrors && noRoles) || mutateError) && (
            <Paragraph weight="medium" color="danger" align="center">
              {mutateError ?? noRoles}
            </Paragraph>
          )}
        </Stack>
      </main>
      <Footer>
        <FooterButtons>
          <Button
            variant="secondary"
            size="medium"
            isLoading={isPendingSubmit}
            disabled={isPendingSubmit}
            onPress={cancel}
          >
            <Trans id="settings.users.invite-modal.cancel">Cancel</Trans>
          </Button>
          {user ? (
            <Button
              variant="primary"
              size="medium"
              isLoading={isPendingSubmit}
              disabled={isPendingSubmit}
              onPress={handleSubmit}
            >
              <Trans id="settings.users.update-modal.submit">Save</Trans>
            </Button>
          ) : (
            <Button
              variant="primary"
              size="medium"
              isLoading={isPendingSubmit}
              disabled={isPendingSubmit}
              onPress={handleSubmit}
            >
              <Trans id="settings.users.invite-modal.submit">Send invitation</Trans>
            </Button>
          )}
        </FooterButtons>
      </Footer>
    </>
  );
};

const UserRolesInput = ({
  userRoles,
  setUserRoles,
  showErrors,
}: {
  userRoles: UserRole[];
  setUserRoles: (userRoles: UserRole[]) => void;
  showErrors: boolean;
}) => {
  const { t } = useLingui();
  const [currentOrganization] = useCurrentOrganization();

  const { data: { roles: availableRoles } = { roles: [] } } = useRoles({ limit: 500 });

  const options = availableRoles
    .map((role) => ({
      value: role.id,
      label: role.name,
      role,
      canBeAddedMultipleTimes: role.scopeStore || role.scopeWorkshop || role.scopeAfterSales,
    }))
    .filter((role) => {
      if (!role.canBeAddedMultipleTimes) {
        return !userRoles.some((userRole) => userRole.roleId === role.value);
      }

      return true;
    });

  const showEntityColumn = !!currentOrganization;

  return (
    <Stack gap="0.5rem">
      <Table
        ariaLabel={t({
          id: 'settings.users.invite-modal.table-aria-label',
          message: 'User roles',
        })}
        columnWidths={['minmax(200px, 1fr)', showEntityColumn && 'minmax(200px, 1fr)', '90px']}
      >
        <Header>
          <Row>
            <Column>
              <Trans id="settings.users.invite-modal.column.roles">Roles</Trans>
            </Column>
            {showEntityColumn && (
              <Column>
                <Trans id="settings.users.invite-modal.column.entity">Entity</Trans>
              </Column>
            )}
            <Column />
          </Row>
        </Header>
        <Body>
          {userRoles.map((userRole) => {
            const shouldShowEntityInput =
              userRole.role.scopeStore ||
              userRole.role.scopeWorkshop ||
              userRole.role.scopeAfterSales;

            const setUserRole = (userRole: UserRole) =>
              setUserRoles(userRoles.map((ur) => (ur.id === userRole.id ? userRole : ur)));

            return (
              <Row key={userRole.id}>
                <Cell>{userRole.role.name}</Cell>
                {showEntityColumn && (
                  <Cell stretch>
                    {shouldShowEntityInput ? (
                      <EntityInput
                        userRole={userRole}
                        setUserRole={setUserRole}
                        userRoles={userRoles}
                        showErrors={showErrors}
                      />
                    ) : (
                      '-'
                    )}
                  </Cell>
                )}
                <Cell justifyContent="flex-end">
                  <Button
                    variant="tertiary"
                    size="medium"
                    ariaLabel={t({
                      id: 'settings.users.invite-modal.remove-role',
                      message: 'Remove role',
                    })}
                    iconOnly
                    onPress={() => {
                      setUserRoles(userRoles.filter((ur) => ur.id !== userRole.id));
                    }}
                  >
                    <IconCross />
                  </Button>
                </Cell>
              </Row>
            );
          })}
        </Body>
      </Table>
      <InputSelect
        options={options}
        aria-label={t({ id: 'settings.users.invite-modal.select-role', message: 'Select a role' })}
        placeholder={t({ id: 'settings.users.invite-modal.select-role', message: 'Select a role' })}
        getOptionValue={(option) => option?.value ?? ''}
        getOptionLabel={(option) => option?.label ?? ''}
        value={null}
        onChange={(selected) => {
          if (!selected) return;

          setUserRoles([
            ...userRoles,
            {
              id: `new-${Math.random()}`,
              roleId: selected.role.id,
              role: selected.role,
              organizationId: currentOrganization?.id ?? null,
              entityId: null,
              entity: null,
            },
          ]);
        }}
      />
    </Stack>
  );
};

const EntityInput = ({
  userRole,
  setUserRole,
  userRoles,
  showErrors,
}: {
  userRole: UserRole;
  setUserRole: (userRole: UserRole) => void;
  userRoles: UserRole[];
  showErrors: boolean;
}) => {
  const trait = userRole.role.scopeStore
    ? 'store'
    : userRole.role.scopeWorkshop
      ? 'workshop'
      : userRole.role.scopeAfterSales
        ? 'aftersales'
        : undefined;

  const { t } = useLingui();
  const [searchEntity, debouncedSearchEntity, setSearchEntity] = useDebouncedState('', 300);
  const { data: { entities } = { entities: [] }, isFetching: isFetchingEntities } = useEntities({
    /**
     * This is a hack to allow to add users to external stores.
     * Once embedded external stores are properly supported, this should be removed.
     */
    external: trait === 'store' ? undefined : false,
    traits: trait ? [trait] : undefined,
    search: debouncedSearchEntity,
    limit: 500,
  });

  const otherUserRoleWithSameRole = userRoles.filter(
    (ur) => ur.roleId === userRole.roleId && ur.id !== userRole.id
  );

  const options = entities
    .filter((entity) => !otherUserRoleWithSameRole.some((ur) => ur.entityId === entity.id))
    .map((entity) => ({
      value: entity.id,
      label: entity.name,
    }));

  return (
    <InputSelect
      aria-label={t({
        id: 'settings.users.invite-modal.select-entity',
        message: 'Select an entity',
      })}
      placeholder={t({
        id: 'settings.users.invite-modal.select-entity',
        message: 'Select an entity',
      })}
      filterOption={null}
      inputValue={searchEntity}
      onInputChange={setSearchEntity}
      isLoading={isFetchingEntities}
      value={
        userRole.entityId
          ? {
              value: userRole.entityId,
              label: userRole.entity!.name,
            }
          : null
      }
      onChange={(selected) => {
        if (!selected) return;

        setUserRole({
          ...userRole,
          entityId: selected.value,
          entity: {
            name: selected.label,
          },
        });
      }}
      options={options}
      error={
        showErrors && !userRole.entityId
          ? t({
              id: 'settings.users.invite-modal.error.no-entity',
              message: 'Entity is required',
            })
          : undefined
      }
    />
  );
};
